import axios, {AxiosInstance, AxiosRequestConfig} from 'axios';

import {WebConfig} from '@buncha/utils/types';
import axiosRetry from 'axios-retry';
import {getConfig} from '@buncha/utils/appInfo';

const apiClient: AxiosInstance = axios.create({
  baseURL: getConfig(WebConfig.b2BMeijerURL), // Replace with your actual base URL
  timeout: 200000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add retry logic
axiosRetry(apiClient, {
  retries: 3,
  retryDelay: (retryCount: number) => {
    return retryCount * 1000;
  },
  retryCondition: error => {
    const status = error?.response?.status;
    return (
      axiosRetry.isNetworkError(error) ||
      axiosRetry.isRetryableError(error) ||
      (typeof status === 'number' && status >= 500 && status <= 599)
    );
  },
});

// Auth parameters stored locally in the same file
let authParams = {
  contactId: '',
  orderId: '',
};

export const setAuthParams = (contactId: string, orderId: string) => {
  authParams = {contactId, orderId};
  sessionStorage.setItem('authParams', JSON.stringify(authParams));
};

// interceptors
apiClient.interceptors.request.use(
  config => {
    // Add auth token or secrete value here if needed
    // Inject authParams into headers
    const savedParams = sessionStorage.getItem('authParams');
    if (savedParams) authParams = JSON.parse(savedParams);

    if (authParams.contactId)
      config.headers['x-contact-id'] = authParams.contactId;

    if (authParams.orderId) config.headers['x-order-id'] = authParams.orderId;

    return config;
  },
  async error => Promise.reject(error),
);

export const apiService = {
  get: async <T>(
    url: string,
    params: object = {},
    config: AxiosRequestConfig = {},
  ): Promise<T> => {
    const response = await apiClient.get<T>(url, {params, ...config});
    return response.data;
  },

  post: async <T>(
    url: string,
    data: object = {},
    config: AxiosRequestConfig = {},
  ): Promise<T> => {
    const response = await apiClient.post<T>(url, data, {...config});
    return response.data;
  },

  put: async <T>(
    url: string,
    data: object = {},
    config: AxiosRequestConfig = {},
  ): Promise<T> => {
    const response = await apiClient.put<T>(url, data, {...config});
    return response.data;
  },

  delete: async <T>(
    url: string,
    config: AxiosRequestConfig = {},
  ): Promise<T> => {
    const response = await apiClient.delete<T>(url, {...config});
    return response.data;
  },
};
